import React, { useState, useRef, useEffect } from 'react';
import supabase from './supabaseClient';
import { usePremiumAccess } from './hooks/usePremiumAccess';
import { PremiumUpgradePrompt } from './components/PremiumGate';

interface ProfilePictureUploadProps {
  telegramUserId: string;
  clientName: string;
  currentPictureUrl?: string;
  onPictureUpdated: (url: string) => void;
  isLoading?: boolean;
  onUpgrade?: () => void;
}

export default function ProfilePictureUpload({ 
  telegramUserId, 
  clientName, 
  currentPictureUrl, 
  onPictureUpdated,
  isLoading = false,
  onUpgrade,
}: ProfilePictureUploadProps) {
  const { isPremium, loading: premiumLoading } = usePremiumAccess();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentPictureUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update preview when currentPictureUrl prop changes
  useEffect(() => {
    setPreviewUrl(currentPictureUrl || null);
  }, [currentPictureUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isPremium) return;

    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfilePicture = async (file: File) => {
    try {
      setUploading(true);

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${telegramUserId}_${Date.now()}.${fileExt}`;
      const filePath = `profile-pictures/${fileName}`;

      console.log('Attempting upload to:', filePath);

      // Upload to Supabase Storage with simpler approach
      const { data, error } = await supabase.storage
        .from('client-profiles')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true // Allow overwriting
        });

      if (error) {
        console.error('Storage upload error:', error);
        throw error;
      }

      console.log('Upload successful:', data);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('client-profiles')
        .getPublicUrl(filePath);

      console.log('Public URL:', publicUrl);

      // First, check if a profile record already exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('client_profiles')
        .select('id')
        .eq('telegram_user_id', telegramUserId)
        .single();

      let profileError;
      
      if (checkError && checkError.code === 'PGRST116') {
        // No existing record, insert new one
        const { error: insertError } = await supabase
          .from('client_profiles')
          .insert({
            telegram_user_id: telegramUserId,
            profile_picture_url: publicUrl,
            updated_at: new Date().toISOString()
          });
        profileError = insertError;
      } else if (existingProfile) {
        // Record exists, update it
        const { error: updateError } = await supabase
          .from('client_profiles')
          .update({
            profile_picture_url: publicUrl,
            updated_at: new Date().toISOString()
          })
          .eq('telegram_user_id', telegramUserId);
        profileError = updateError;
      } else {
        // Some other error occurred during check
        profileError = checkError;
      }

      if (profileError) {
        console.error('Database update error:', profileError);
        throw profileError;
      }

      onPictureUpdated(publicUrl);
      alert('Profile picture updated successfully! 📸');

    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      
      // More specific error messages
      if (error.message?.includes('infinite recursion')) {
        alert('Storage policy error. Please contact support to fix bucket permissions.');
      } else if (error.message?.includes('bucket')) {
        alert('Storage bucket not found. Please ensure the client-profiles bucket exists.');
      } else if (error.message?.includes('duplicate key')) {
        alert('Profile already exists. The system will update the existing profile.');
      } else {
        alert(`Error uploading profile picture: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (file) {
      await uploadProfilePicture(file);
    }
  };

  const removeProfilePicture = async () => {
    try {
      setUploading(true);

      // Remove from database
      const { error } = await supabase
        .from('client_profiles')
        .update({
          profile_picture_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('telegram_user_id', telegramUserId);

      if (error) {
        throw error;
      }

      setPreviewUrl(null);
      onPictureUpdated('');
      alert('Profile picture removed successfully! 🗑️');

    } catch (error: any) {
      console.error('Error removing profile picture:', error);
      alert('Error removing profile picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="retro-card p-4">
      <div className="index-tab">
        <h3 className="text-lg font-semibold text-[#1f1f1f]">📸 Profile Picture</h3>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Current Profile Picture */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            {isLoading ? (
              <div className="w-20 h-20 bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                <div className="loading"></div>
              </div>
            ) : previewUrl && isPremium ? (
              <img
                src={previewUrl}
                alt={`${clientName}'s profile`}
                className="w-20 h-20 rounded-full object-cover border-2 border-[#0eeaff] shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                {getInitials(clientName)}
              </div>
            )}
            
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="text-white text-sm">Uploading...</div>
              </div>
            )}
          </div>
          
          <div>
            <h4 className="font-semibold text-[#1f1f1f]">{clientName}</h4>
            <p className="text-sm text-[#666]">
              {isLoading || premiumLoading ? 'Loading profile picture...' :
               isPremium && previewUrl ? 'Profile picture uploaded' : 'No profile picture'}
            </p>
          </div>
        </div>

        {/* Upload Controls */}
        {!isPremium && !premiumLoading ? (
          <PremiumUpgradePrompt
            featureName="Client Profile Pictures"
            description="Upload avatars that appear on your dashboard, client list, and leaderboard."
            onUpgrade={onUpgrade}
            compact
          />
        ) : (
        <>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#1f1f1f] mb-2">
              Upload New Picture
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="vintage-input w-full"
              disabled={uploading}
            />
            <p className="text-xs text-[#666] mt-1">
              Supported formats: JPG, PNG, GIF. Max size: 5MB
            </p>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleUpload}
              disabled={!fileInputRef.current?.files?.[0] || uploading}
              className={`neon-button flex-1 ${
                !fileInputRef.current?.files?.[0] || uploading 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:scale-105'
              }`}
            >
              {uploading ? '📤 Uploading...' : '📤 Upload Picture'}
            </button>
            
            {previewUrl && (
              <button
                onClick={removeProfilePicture}
                disabled={uploading}
                className={`bg-[#ff6b6b] hover:bg-[#ff5252] text-white px-4 py-2 rounded transition-colors ${
                  uploading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                }`}
              >
                🗑️ Remove
              </button>
            )}
          </div>
        </div>

        <div className="bg-[#8aff8a]/10 border border-[#8aff8a] rounded-lg p-3">
          <p className="text-xs text-[#2d2d2d]">
            <strong>Privacy Note:</strong> Profile pictures are stored securely and only visible to you and your trainer. 
            They may be used in anonymized social comparisons if you opt-in to social features.
          </p>
        </div>
        </>
        )}
      </div>
    </div>
  );
} 